import React, { useState, useRef, useEffect, useContext, useCallback, useMemo } from 'react';
import { Platform, useWindowDimensions, View } from 'react-native';
import { Text, ActivityIndicator } from 'react-native-paper';
import { FlashList } from '@shopify/flash-list';
import { useSelector, useDispatch } from 'react-redux';
import {
    collection,
    query,
    orderBy,
    limit,
    onSnapshot,
    startAfter,
    getFirestore
} from 'firebase/firestore';

import Global from "../constants/global";
import Preferances from '../constants/Preferances';
import PostRow from "./PostRow";
import { listsTask } from '../task/listTasks';

const PAGE_SIZE = 3;

const Posts = React.memo(({ navigation }) => {
    const dispatch = useDispatch();
    const pref = useContext(Preferances);

    // 1. Get raw data from Redux
    const rawPosts = useSelector(s => s.list.Posts);

    // 2. Local state for Pagination
    const [loading, setLoading] = useState(false);
    const [lastDoc, setLastDoc] = useState(null);
    const [isReachingEnd, setIsReachingEnd] = useState(false);

    // 3. Keep track of active listeners to kill them on unmount
    const unsubscribers = useRef([]);

    // 4. Memoized Filter Logic (Your original logic)
    const filteredList = useMemo(() => {
        return rawPosts.filter(e =>
            e.type === 0
            || (e.type === 1 && (pref?.current?.type === Global.PRINCIPAL || pref?.current?.type === Global.TRUSTEE))
            || (e.type === 2 && (pref?.current?.category === Global.CAT_CORE_TEAM))
            || (e.type === 3 && (pref?.current?.type === Global.TEACHER))
            || (e.type === 4 && (pref?.current?.type?.isClassTeacher))
            || (e.type === 5 && (pref?.current?.type === Global.STUDENT))
            || (e.type === 6 && (pref?.current?.type === Global.PARENT))
            || (e.type === 7 && (pref?.current?.type === Global.GUEST))
        );
    }, [rawPosts, pref]);

    // 5. Core Pagination Listener Function
    const fetchPage = useCallback((cursor = null) => {
        if (loading || isReachingEnd) return;

        setLoading(true);
        let q = query(
            collection(getFirestore(), "Posts"), // Adjust to your collection name
            orderBy("stamp", "desc"),
            limit(PAGE_SIZE)
        );

        if (cursor) {
            q = query(q, startAfter(cursor));
        }

        const unsub = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Dispatch to Redux - Reducer handles merging/sorting
            dispatch(listsTask.actions.appendPosts({ payload: items }));

            // Determine if we've hit the end of the database
            if (snapshot.docs.length < PAGE_SIZE) {
                setIsReachingEnd(true);
            }

            // Save the last document snapshot for the next pagination trigger
            if (snapshot.docs.length > 0) {
                setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
            }
            setLoading(false);
        }, (err) => {
            console.error("Firestore Listener Error:", err);
            setLoading(false);
        });

        unsubscribers.current.push(unsub);
    }, [loading, isReachingEnd, dispatch]);

    // 6. Initial Load & Cleanup
    useEffect(() => {
        fetchPage(); // Load first 10

        return () => {
            // Clean up ALL listeners when user leaves the screen
            unsubscribers.current.forEach(unsub => unsub());
            unsubscribers.current = [];
        };
    }, []);

    // 7. Footer Loader
    const renderFooter = () => (
        loading ? <ActivityIndicator style={{ padding: 20 }} /> : null
    );


    const { width } = useWindowDimensions();
    const isDesktop = Platform.OS === 'web' && width > 900
    return (
        <View style={{ backgroundColor: "transparent", flex: 1, alignSelf: "stretch" }}>
            <FlashList
                data={filteredList}
                keyExtractor={(item) => item.id}
                estimatedItemSize={500}
                numColumns={isDesktop ? 2 : 1}   // 👈 two columns on desktop
                columnWrapperStyle={isDesktop ? { justifyContent: 'space-between' } : null}
                renderItem={({ item }) => (
                    <View style={{ flex: 1, margin: 8 }}>
                        <PostRow
                            login={pref.current}
                            navigation={navigation}
                            row={item}
                        />
                    </View>
                )}
                onEndReached={() => fetchPage(lastDoc)}
                onEndReachedThreshold={0.5}
            />

        </View>
    );
});

export default Posts;